/**
 * Signature fields on forms (TRUS-763).
 *
 * A signature field's value is the PNG data URL from the signature pad. Beside
 * it, `<field_id>__signature` holds the signature record: who signed, how,
 * their consent, and WHEN. Each signature field has its own record, so a
 * member and a care manager who sign on different days each keep their own
 * date. The backend stamps `signed_at` and `signed_by_user_id`; the client only
 * supplies what the pad collected.
 *
 * Legacy values such as "Signed" or "Yes" (copied from old member fields) are
 * NOT signatures and are treated as unsigned.
 */
import type { SignatureResult } from '@/components/signatures/SignaturePad';

export const SIGNATURE_META_SUFFIX = '__signature';

export interface SignatureRecord {
  signer_name?: string | null;
  signature_type?: 'drawn' | 'typed' | 'uploaded' | string | null;
  consent_accepted?: boolean | null;
  signed_at?: string | null;
  /** Calendar date of the signature (signer's local date, checked by the server). */
  signed_on?: string | null;
  /** Sent by the client; the server decides `signed_on` from it. */
  signed_local_date?: string | null;
  signed_by_user_id?: number | null;
  added_after_submit?: boolean;
}

export const signatureMetaKey = (fieldId: string) => `${fieldId}${SIGNATURE_META_SUFFIX}`;

export const isSignatureImage = (value: unknown): value is string =>
  typeof value === 'string' && value.trimStart().toLowerCase().startsWith('data:image');

/** Legacy text ("Signed", "Yes", a typed name) that only claims a signature. */
export const isLegacySignatureText = (value: unknown): boolean =>
  typeof value === 'string' && value.trim() !== '' && !isSignatureImage(value);

export const readSignatureRecord = (
  data: Record<string, any> | undefined | null,
  fieldId: string,
): SignatureRecord | null => {
  const rec = data?.[signatureMetaKey(fieldId)];
  return rec && typeof rec === 'object' ? (rec as SignatureRecord) : null;
};

/** Today's date in the signer's own time zone, YYYY-MM-DD. */
export const localDateString = (d: Date = new Date()): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/**
 * What the pad collected, for the signature record. The server sets `signed_at`
 * and decides `signed_on` (from `signed_local_date` when plausible); `signed_on`
 * here only shows the date until the form is saved.
 */
export const recordFromPad = (result: SignatureResult): SignatureRecord => {
  const today = localDateString();
  return {
    signer_name: result.signer_name,
    signature_type: result.signature_type,
    consent_accepted: result.consent_accepted,
    signed_local_date: today,
    signed_on: today,
  };
};

/**
 * The date field a signature fills (`signature_date_field` on the template
 * field). Signed digitally -> filled and locked; on paper the LCM enters it.
 */
export const signatureDateFieldId = (field: { signature_date_field?: string } | any): string | null =>
  (field && typeof field.signature_date_field === 'string' && field.signature_date_field) || null;

/**
 * Date fields locked because their signature was made digitally. A signature
 * with no record (made before records existed) leaves its date editable.
 */
export const lockedSignatureDateFieldIds = (
  sections: { fields?: any[] }[] | undefined,
  data: Record<string, any> | undefined | null,
): Set<string> => {
  const locked = new Set<string>();
  for (const section of sections || []) {
    for (const field of section.fields || []) {
      const dateId = signatureDateFieldId(field);
      if (
        dateId &&
        field.field_type === 'signature' &&
        isSignatureImage(data?.[field.field_id]) &&
        readSignatureRecord(data, field.field_id)?.signed_on
      ) {
        locked.add(dateId);
      }
    }
  }
  return locked;
};

const MEMBER_SIGNER = /\b(member|client|participant|patient|guardian|caregiver)\b/i;

/**
 * Who is expected to sign this field: the member for a member/client signature,
 * otherwise the logged-in staff member. Used only to prefill the pad's name.
 */
export const defaultSignerName = (
  field: { field_id: string; label?: string },
  names: { memberName?: string | null; staffName?: string | null },
): string => {
  const text = `${field.label || ''} ${field.field_id.replace(/_/g, ' ')}`;
  return (MEMBER_SIGNER.test(text) ? names.memberName : names.staffName) || '';
};
